import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Input, Select, message, Modal, Tabs, Tooltip, Tag } from "antd";
import {
  SaveOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
  CopyOutlined,
  ExpandOutlined,
  CompressOutlined,
  InfoCircleOutlined,
  PrinterOutlined,
  PlusOutlined,
  PictureOutlined,
  CodeOutlined,
  DatabaseOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { canWrite } from "../../helpers/permissions";
import "./index.css";

const { Option } = Select;
const { TextArea } = Input;

// Default images from public folder
const DEFAULT_IMAGES = [
  { id: "wow-wednesday", name: "WOW Wednesday", path: "/wow-wednesday-logo.png" },
  { id: "rapsap-loyal", name: "Rapsap Loyal", path: "/rapsap-loyal-logo.png" },
  { id: "rapsap-icon", name: "Rapsap Icon", path: "/rapsap_icon.png" },
  { id: "rapsap-black", name: "Rapsap Black Logo", path: "/rapsap-black-logo.png" },
];

// Available variables that can be used in templates
const AVAILABLE_VARIABLES = [
  { key: "itemName", label: "Item Name", field: "Item Name" },
  { key: "brand", label: "Brand", field: "Brand" },
  { key: "category", label: "Category", field: "Categories" },
  { key: "mrp", label: "MRP", field: "MRP" },
  { key: "price", label: "Price", field: "Rapsap Price" },
  { key: "savings", label: "Savings Amount", computed: true },
  { key: "savingsPercent", label: "Savings %", computed: true },
  { key: "loyalPrice", label: "Loyal Price (Cafe)", cafe: true },
  { key: "calories", label: "Calories (Cafe)", cafe: true },
  { key: "vegNonVeg", label: "Veg/Non-Veg Text (Cafe)", cafe: true },
  { key: "vegIcon", label: "Veg/Non-Veg Icon (Cafe)", computed: true, cafe: true },
  { key: "vegColor", label: "Veg Color Hex (Cafe)", computed: true, cafe: true },
  { key: "vegLabel", label: "Veg Label (Cafe)", computed: true, cafe: true },
  { key: "caloriesBadge", label: "Calories Badge (Cafe)", computed: true, cafe: true },
];

// Sample product data for preview
const SAMPLE_PRODUCT = {
  "Item Name": "Itc Master Chef Grilled Chicken Wing 360Gm",
  Brand: "ITC",
  Categories: "Frozen Foods",
  MRP: "90",
  "Rapsap Price": "80",
  Savings: "10",
};

// Default starter template (500x200px - same as Medium Card)
const DEFAULT_TEMPLATE = `<div style="width: 500px; height: 200px; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden; display: flex; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); font-family: 'DM Sans', sans-serif;">
  <!-- Left Section -->
  <div style="flex: 1; display: flex; flex-direction: column; justify-content: space-between; padding: 16px;">
    <!-- Top Row -->
    <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;">
      <div style="font-size: 19px; font-weight: 500; color: #1a1a1a; line-height: 1.3; flex: 1; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;">
        {{itemName}}
      </div>
      <div style="text-align: right;">
        <div style="font-size: 16px; font-weight: 700; color: #1a1a1a;">Our Price : ₹{{price}}</div>
        <div style="font-family: 'Barlow Condensed', sans-serif; font-size: 14px; font-style: italic; color: #9ca3af; text-decoration: line-through;">MRP: ₹{{mrp}}</div>
      </div>
    </div>
    <!-- Savings Display -->
    <div style="display: flex; align-items: baseline; gap: 4px;">
      <span style="font-family: 'Barlow Condensed', sans-serif; font-size: 28px; font-weight: 600; font-style: italic; color: #1a1a1a;">₹</span>
      <span style="font-family: 'Barlow Condensed', sans-serif; font-size: 65px; font-weight: 700; font-style: italic; color: #1a1a1a;">{{savings}}</span>
      <span style="font-family: 'Barlow Condensed', sans-serif; font-size: 26px; font-weight: 500; font-style: italic; color: #1a1a1a; margin-left: 8px;">savings</span>
    </div>
  </div>
  <!-- Right Section -->
  <div style="width: 200px; flex-shrink: 0; overflow: hidden;">
    <img src="{{image1}}" alt="Logo" style="width: 100%; height: 100%; object-fit: cover;" />
  </div>
</div>`;

// Replace template variables with actual values
const processTemplate = (template, data, images = {}) => {
  let processed = template;

  // Detect format: Cafe CSV has "PRODUCT NAME", Grocery has "Item Name"
  const isCafe = "PRODUCT NAME" in data;

  // Calculate computed values (handle both Grocery and Cafe price fields)
  const mrp = parseFloat(data.MRP) || 0;
  const price = isCafe ? (parseFloat(data.LYP) || 0) : (parseFloat(data["Rapsap Price"]) || 0);
  const savings = mrp - price;
  const savingsPercent = mrp > 0 ? Math.round((savings / mrp) * 100) : 0;
  const itemName = isCafe ? (data["PRODUCT NAME"] || "") : (data["Item Name"] || "");

  // Dynamic font size for long item names
  // Cafe names are uppercase — use lower thresholds
  const longThreshold = isCafe ? 30 : 45;
  const medThreshold = isCafe ? 22 : 35;
  let nameHtml = itemName;
  if (itemName.length > longThreshold) {
    nameHtml = `<span style="font-size: 0.7em">${itemName}</span>`;
  } else if (itemName.length > medThreshold) {
    nameHtml = `<span style="font-size: 0.85em">${itemName}</span>`;
  }

  // Common variables
  processed = processed.replace(/\{\{itemName\}\}/g, nameHtml);
  processed = processed.replace(/\{\{mrp\}\}/g, Math.round(mrp));
  processed = processed.replace(/\{\{price\}\}/g, Math.round(price));
  processed = processed.replace(/\{\{savings\}\}/g, Math.round(savings));
  processed = processed.replace(/\{\{savingsPercent\}\}/g, savingsPercent);

  // Grocery-specific
  processed = processed.replace(/\{\{brand\}\}/g, data.Brand || "");
  processed = processed.replace(/\{\{category\}\}/g, data.Categories || "");

  // Cafe-specific
  processed = processed.replace(/\{\{loyalPrice\}\}/g, Math.round(parseFloat(data.LYP) || 0));
  processed = processed.replace(/\{\{calories\}\}/g, data.CALS || "");
  processed = processed.replace(/\{\{vegNonVeg\}\}/g, data["VEG / NON"] || "");
  const isVeg = (data["VEG / NON"] || "").toUpperCase() === "VEG";
  processed = processed.replace(/\{\{vegColor\}\}/g, isVeg ? "#22BD03" : "#EF4444");
  processed = processed.replace(/\{\{vegLabel\}\}/g, isVeg ? "VEG" : "NON-VEG");

  // Veg/Non-veg icon HTML
  const vegIconHtml = isVeg
    ? `<div style="width:32px;height:32px;border-radius:4px;border:3px solid #22BD03;display:flex;align-items:center;justify-content:center;"><div style="width:16px;height:16px;background:#22BD03;border-radius:9999px;"></div></div>`
    : `<div style="width:32px;height:32px;border-radius:4px;border:3px solid #EF4444;display:flex;align-items:center;justify-content:center;"><div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-bottom:14px solid #EF4444;"></div></div>`;
  processed = processed.replace(/\{\{vegIcon\}\}/g, vegIconHtml);

  // Calories badge HTML
  const calsValue = data.CALS || "";
  const calsBadgeHtml = calsValue
    ? `<div style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f5f5f5;border-radius:12px;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;color:#666;"><span>${calsValue}</span><span style="font-size:11px;">cal</span></div>`
    : "";
  processed = processed.replace(/\{\{caloriesBadge\}\}/g, calsBadgeHtml);

  // Replace image placeholders
  processed = processed.replace(/\{\{image1\}\}/g, images.image1 || "/wow-wednesday-logo.png");

  return processed;
};

// Extract dimensions from template HTML
const extractDimensions = (html) => {
  const widthMatch = html.match(/width:\s*(\d+)px/);
  const heightMatch = html.match(/height:\s*(\d+)px/);
  return {
    width: widthMatch ? parseInt(widthMatch[1]) : 262,
    height: heightMatch ? parseInt(heightMatch[1]) : 113,
  };
};

export default function TemplateBuilder() {
  const [htmlCode, setHtmlCode] = useState(DEFAULT_TEMPLATE);
  const [templateName, setTemplateName] = useState("New Template");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [sampleData, setSampleData] = useState(SAMPLE_PRODUCT);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState("html");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [templateImages, setTemplateImages] = useState({
    image1: "/wow-wednesday-logo.png",
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const iframeRef = useRef(null);
  const imageInputRef = useRef(null);

  // Load uploaded images from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("templateBuilderImages");
    if (saved) {
      setUploadedImages(JSON.parse(saved));
    }
  }, []);

  // Combine default and uploaded images
  const availableImages = [...DEFAULT_IMAGES, ...uploadedImages];

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      message.error("Please select an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result;
      const newImage = {
        id: `uploaded-${Date.now()}`,
        name: file.name.replace(/\.[^/.]+$/, ""),
        path: base64,
        isUploaded: true,
      };

      const updated = [...uploadedImages, newImage];
      setUploadedImages(updated);
      localStorage.setItem("templateBuilderImages", JSON.stringify(updated));
      message.success(`Uploaded "${file.name}"`);
    };
    reader.readAsDataURL(file);

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  // Delete uploaded image
  const handleDeleteImage = (imageId) => {
    const updated = uploadedImages.filter((img) => img.id !== imageId);
    setUploadedImages(updated);
    localStorage.setItem("templateBuilderImages", JSON.stringify(updated));
    message.success("Image deleted");
  };

  // Load saved templates from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("shelfTalkerTemplates");
    if (saved) {
      setSavedTemplates(JSON.parse(saved));
    }
  }, []);

  // Update preview whenever HTML changes
  const updatePreview = useCallback(() => {
    if (iframeRef.current) {
      const processedHtml = processTemplate(htmlCode, sampleData, templateImages);
      const iframeDoc = iframeRef.current.contentDocument;
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap" rel="stylesheet">
            <style>
              body {
                margin: 0;
                padding: 20px;
                background: #f5f5f5;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: calc(100vh - 40px);
              }
            </style>
          </head>
          <body>${processedHtml}</body>
        </html>
      `);
      iframeDoc.close();
    }
  }, [htmlCode, sampleData, templateImages]);

  useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  // Save template to localStorage
  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      message.error("Please enter a template name");
      return;
    }

    const dimensions = extractDimensions(htmlCode);
    const newTemplate = {
      id: Date.now().toString(),
      name: templateName,
      html: htmlCode,
      images: templateImages,
      width: dimensions.width,
      height: dimensions.height,
      createdAt: new Date().toISOString(),
    };

    const existingIndex = savedTemplates.findIndex((t) => t.name === templateName);
    let updatedTemplates;

    if (existingIndex >= 0) {
      updatedTemplates = [...savedTemplates];
      updatedTemplates[existingIndex] = newTemplate;
      message.success("Template updated successfully");
    } else {
      updatedTemplates = [...savedTemplates, newTemplate];
      message.success("Template saved successfully");
    }

    setSavedTemplates(updatedTemplates);
    localStorage.setItem("shelfTalkerTemplates", JSON.stringify(updatedTemplates));
    setShowSaveModal(false);
  };

  // Load template
  const handleLoadTemplate = (template) => {
    setHtmlCode(template.html);
    setTemplateName(template.name);
    if (template.images) {
      setTemplateImages(template.images);
    }
    setShowLoadModal(false);
    message.success(`Loaded "${template.name}"`);
  };

  // Delete template
  const handleDeleteTemplate = (templateId) => {
    const updatedTemplates = savedTemplates.filter((t) => t.id !== templateId);
    setSavedTemplates(updatedTemplates);
    localStorage.setItem("shelfTalkerTemplates", JSON.stringify(updatedTemplates));
    message.success("Template deleted");
  };

  // Copy variable to clipboard
  const copyVariable = (variable) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    message.success(`Copied {{${variable}}} to clipboard`);
  };

  // Insert variable at cursor
  const insertVariable = (variable) => {
    const textarea = document.querySelector(".code-editor");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = `{{${variable}}}`;
      const newCode = htmlCode.slice(0, start) + text + htmlCode.slice(end);
      setHtmlCode(newCode);

      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + text.length, start + text.length);
      }, 0);
    }
  };

  // Export template for use in ShelfTalker
  const handleExportTemplate = () => {
    const dimensions = extractDimensions(htmlCode);
    const templateCode = `
// Custom Template - ${templateName}
// Dimensions: ${dimensions.width}x${dimensions.height}px
const CustomCard = ({ item }) => {
  const itemName = item["Item Name"] || "";
  const mrp = Math.round(parseFloat(item.MRP) || 0);
  const price = Math.round(parseFloat(item["Rapsap Price"]) || 0);
  const savings = mrp - price;
  const savingsPercent = mrp > 0 ? Math.round((savings / mrp) * 100) : 0;

  return (
    <div dangerouslySetInnerHTML={{ __html: \`${htmlCode
      .replace(/\{\{itemName\}\}/g, "${itemName}")
      .replace(/\{\{brand\}\}/g, '${item.Brand || ""}')
      .replace(/\{\{category\}\}/g, '${item.Categories || ""}')
      .replace(/\{\{mrp\}\}/g, "${mrp}")
      .replace(/\{\{price\}\}/g, "${price}")
      .replace(/\{\{savings\}\}/g, "${savings}")
      .replace(/\{\{savingsPercent\}\}/g, "${savingsPercent}")
      .replace(/`/g, "\\`")}\` }} />
  );
};
`;

    navigator.clipboard.writeText(templateCode);
    message.success("React component code copied to clipboard!");
  };

  // Test print with current template
  const handleTestPrint = () => {
    const processedHtml = processTemplate(htmlCode, sampleData);
    const dimensions = extractDimensions(htmlCode);

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Template Preview - ${templateName}</title>
          <link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&display=swap" rel="stylesheet">
          <style>
            body {
              margin: 0;
              padding: 20px;
              background: #e0e0e0;
              font-family: 'DM Sans', sans-serif;
            }
            .preview-header {
              background: #fff;
              padding: 16px 24px;
              margin-bottom: 20px;
              border-radius: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              max-width: 794px;
              margin: 0 auto 20px;
            }
            .preview-header h1 { font-size: 18px; font-weight: 500; margin: 0; }
            .preview-header span { color: #666; font-size: 14px; }
            .a4-page {
              width: 210mm;
              min-height: 297mm;
              background: #fff;
              margin: 0 auto;
              padding: 6mm;
              box-shadow: 0 4px 20px rgba(0,0,0,0.15);
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              align-content: flex-start;
            }
            .print-btn {
              background: #4F7CFF;
              color: #fff;
              border: none;
              padding: 10px 24px;
              border-radius: 6px;
              cursor: pointer;
            }
            @media print {
              body { background: #fff; padding: 0; }
              .preview-header { display: none; }
              .a4-page {
                box-shadow: none;
                width: 210mm;
                height: 297mm;
              }
            }
          </style>
        </head>
        <body>
          <div class="preview-header">
            <div>
              <h1>${templateName} - Print Preview</h1>
              <span>Dimensions: ${dimensions.width}x${dimensions.height}px</span>
            </div>
            <button class="print-btn" onclick="window.print()">Print PDF</button>
          </div>
          <div class="a4-page">
            ${Array(12).fill(processedHtml).join("")}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Update sample data field
  const updateSampleField = (field, value) => {
    setSampleData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className={`template-builder-container ${isFullscreen ? "fullscreen" : ""}`}>
      {/* Header */}
      <div className="builder-header">
        <div className="header-left">
          <h1 className="builder-title">
            template <em>builder</em>
          </h1>
          <Input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="template-name-input"
            placeholder="Template Name"
          />
        </div>
        <div className="header-right">
          <Tooltip title="Help & Variables">
            <Button icon={<InfoCircleOutlined />} onClick={() => setShowHelpModal(true)}>
              Variables
            </Button>
          </Tooltip>
          <Button icon={<FolderOpenOutlined />} onClick={() => setShowLoadModal(true)}>
            Load
          </Button>
          {canWrite() && (
            <Button type="primary" icon={<SaveOutlined />} onClick={() => setShowSaveModal(true)}>
              Save
            </Button>
          )}
          <Button icon={<PrinterOutlined />} onClick={handleTestPrint}>
            Test Print
          </Button>
          <Button icon={<CopyOutlined />} onClick={handleExportTemplate}>
            Export Code
          </Button>
          <Button
            icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="builder-content">
        {/* Left Panel - Editor */}
        <div className="editor-panel">
          <Tabs
            type="card"
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane={false}
            className="icon-tabs"
          >
            <Tabs.TabPane
              tab={
                <span className="tab-label">
                  <CodeOutlined />
                  {activeTab === "html" && <span className="tab-text">HTML</span>}
                </span>
              }
              key="html"
            >
              <div className="editor-wrapper">
                <TextArea
                  className="code-editor"
                  value={htmlCode}
                  onChange={(e) => setHtmlCode(e.target.value)}
                  placeholder="Paste your HTML template here..."
                  spellCheck={false}
                />
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <span className="tab-label">
                  <DatabaseOutlined />
                  {activeTab === "data" && <span className="tab-text">Data</span>}
                </span>
              }
              key="data"
            >
              <div className="sample-data-form">
                <p className="form-hint">Edit sample data to test your template:</p>
                {AVAILABLE_VARIABLES.filter((v) => !v.computed).map((variable) => (
                  <div key={variable.key} className="sample-field">
                    <label>{variable.label}</label>
                    <Input
                      value={sampleData[variable.field] || ""}
                      onChange={(e) => updateSampleField(variable.field, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={
                <span className="tab-label">
                  <PictureOutlined />
                  {activeTab === "images" && <span className="tab-text">Images</span>}
                </span>
              }
              key="images"
            >
              <div className="images-tab-content">
                <div className="images-section">
                  <div className="images-section-title">Template Image</div>
                  <p className="form-hint">Select an image for the template. Use {'{{image1}}'} in your HTML.</p>

                  <div className="template-image-row">
                    {templateImages.image1 ? (
                      <img src={templateImages.image1} alt="image1" />
                    ) : (
                      <div style={{ width: 40, height: 40, background: "#f0f0f0", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <PictureOutlined style={{ color: "#999" }} />
                      </div>
                    )}
                    <div className="template-image-info">
                      <code>{'{{image1}}'}</code>
                      <Select
                        size="small"
                        style={{ width: "100%" }}
                        value={templateImages.image1 || undefined}
                        placeholder="Select image"
                        allowClear
                        onChange={(value) => setTemplateImages(prev => ({ ...prev, image1: value || "" }))}
                      >
                        {availableImages.map((img) => (
                          <Option key={img.id} value={img.path}>{img.name}</Option>
                        ))}
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="images-section">
                  <div className="images-section-header">
                    <div className="images-section-title">Available Images</div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      ref={imageInputRef}
                      style={{ display: "none" }}
                    />
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      onClick={() => imageInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                  </div>
                  <div className="images-grid">
                    {availableImages.map((img) => (
                      <div key={img.id} className="image-item">
                        <img src={img.path} alt={img.name} />
                        <div className="image-item-name">{img.name}</div>
                        {img.isUploaded && (
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            className="image-delete-btn"
                            onClick={() => handleDeleteImage(img.id)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Tabs.TabPane>
          </Tabs>

          {/* Quick Variables Bar */}
          <div className="variables-bar">
            <span className="bar-label">Insert:</span>
            {AVAILABLE_VARIABLES.map((v) => (
              <Tag
                key={v.key}
                className="variable-tag"
                onClick={() => insertVariable(v.key)}
              >
                {`{{${v.key}}}`}
              </Tag>
            ))}
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="preview-panel">
          <div className="preview-header-bar">
            <span>Live Preview</span>
            <span className="preview-dimensions">
              {extractDimensions(htmlCode).width} x {extractDimensions(htmlCode).height}px
            </span>
          </div>
          <div className="preview-iframe-container">
            <iframe
              ref={iframeRef}
              title="Template Preview"
              className="preview-iframe"
            />
          </div>
        </div>
      </div>

      {/* Save Modal */}
      <Modal
        title="Save Template"
        open={showSaveModal}
        onOk={handleSaveTemplate}
        onCancel={() => setShowSaveModal(false)}
        okText="Save"
      >
        <Input
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          placeholder="Enter template name"
          style={{ marginTop: 16 }}
        />
        {savedTemplates.some((t) => t.name === templateName) && (
          <p style={{ color: "#faad14", marginTop: 8 }}>
            A template with this name exists and will be overwritten.
          </p>
        )}
      </Modal>

      {/* Load Modal */}
      <Modal
        title="Load Template"
        open={showLoadModal}
        onCancel={() => setShowLoadModal(false)}
        footer={null}
        width={600}
      >
        {savedTemplates.length === 0 ? (
          <p className="no-templates">No saved templates yet.</p>
        ) : (
          <div className="templates-list">
            {savedTemplates.map((template) => (
              <div key={template.id} className="template-item">
                <div className="template-info">
                  <strong>{template.name}</strong>
                  <span>{template.width}x{template.height}px</span>
                  <span className="template-date">
                    {new Date(template.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="template-actions">
                  <Button size="small" type="primary" onClick={() => handleLoadTemplate(template)}>
                    Load
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteTemplate(template.id)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={() => {
            setHtmlCode(DEFAULT_TEMPLATE);
            setTemplateName("New Template");
            setShowLoadModal(false);
          }}
          style={{ marginTop: 16, width: "100%" }}
        >
          Start with Default Template
        </Button>
      </Modal>

      {/* Help Modal */}
      <Modal
        title="Template Variables"
        open={showHelpModal}
        onCancel={() => setShowHelpModal(false)}
        footer={null}
        width={500}
      >
        <p>Use these variables in your HTML template. They will be replaced with actual product data:</p>
        <div className="variables-help-list">
          {AVAILABLE_VARIABLES.map((v) => (
            <div key={v.key} className="variable-help-item">
              <code onClick={() => copyVariable(v.key)}>{`{{${v.key}}}`}</code>
              <span>{v.label}</span>
              {v.computed && <Tag color="blue">Computed</Tag>}
            </div>
          ))}
        </div>
        <div className="help-tips">
          <h4>Tips:</h4>
          <ul>
            <li>Paste HTML exported from Figma or any design tool</li>
            <li>Use inline styles for best compatibility</li>
            <li>Set explicit width/height on the root element</li>
            <li>Click on a variable to copy it to clipboard</li>
            <li>Test Print shows how cards look on A4 paper</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}
